import { useState } from "react";

import { HudActionButton } from "../../common/HudActionButton";
import {
  type ChannelEnvironment,
  isFailedUnstartedEnvironment,
} from "../useChannelEnvironment";

/** Rare lifecycle operations tucked behind a single "More" toggle.
 *
 *  "Retain & release" is deliberately absent: it was a compatibility alias for
 *  hibernate (node `/retain` → `ChannelEnvManager.hibernate`), so it produced a
 *  `hibernated` environment under a heading promising a distinct `retained`
 *  lifecycle state that nothing has ever written. Hibernate already means
 *  "retain all workspace state, release compute" — one control, honest label. */
export function OverflowActions({
  environment,
  adminAccess = true,
  compact = false,
  onArchive,
  onDetach,
}: {
  environment: ChannelEnvironment;
  /** Non-admins only get the failed-unstarted soft-delete path. */
  adminAccess?: boolean;
  /** Render at the inspector rail's chip scale instead of the full-width one. */
  compact?: boolean;
  onArchive: () => void;
  onDetach: () => void;
}) {
  const size = compact ? "sm" : "md";
  const [open, setOpen] = useState(false);
  const canArchive = adminAccess && environment.status === "running";
  const canDetach = adminAccess
    ? ["errored", "hibernated", "retained", "stopped"].includes(
        environment.status,
      )
    : isFailedUnstartedEnvironment(environment);
  if (!canArchive && !canDetach) {
    // Render-phase reset: without this, an expander left open across a
    // zero-action window (e.g. hibernating) reappears pre-expanded.
    if (open) {
      setOpen(false);
    }
    return null;
  }
  if (!open) {
    return (
      <HudActionButton
        label="More ▾"
        size={size}
        tone="neutral"
        onClick={() => setOpen(true)}
      />
    );
  }
  return (
    <>
      <HudActionButton
        label="Less ▴"
        size={size}
        tone="neutral"
        onClick={() => setOpen(false)}
      />
      {canArchive && (
        <HudActionButton
          label="Hibernate"
          size={size}
          tone="neutral"
          onClick={onArchive}
        />
      )}
      {canDetach && (
        <HudActionButton
          label="Delete env"
          size={size}
          tone="danger"
          onClick={onDetach}
        />
      )}
    </>
  );
}
